import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import styled, { keyframes, css } from 'styled-components'
import { Box } from '@/components/atoms/Grid'

type TopLoadingBarProps = {
  color: string
  height?: number
}

const TopLoadingBar: React.FC<TopLoadingBarProps> = ({ color, height }) => {
  const router = useRouter()
  const [visible, setVisible] = useState<boolean>(false)

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null

    const start = () => {
      if (timeout) {
        clearTimeout(timeout)
      }
      setVisible(true)
    }

    const end = () => {
      timeout = setTimeout(() => setVisible(false), 200)
    }

    router.events.on('routeChangeStart', start)
    router.events.on('routeChangeComplete', end)
    router.events.on('routeChangeError', end)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
      router.events.off('routeChangeStart', start)
      router.events.off('routeChangeComplete', end)
      router.events.off('routeChangeError', end)
    }
  }, [router.events])

  return (
    <BarWrapper visible={visible} height={height}>
      <Bar color={color} />
    </BarWrapper>
  )
}

export default TopLoadingBar

const indeterminate = keyframes`
  0% {
    left: -40%;
    width: 40%;
  }
  50% {
    left: 20%;
    width: 60%;
  }
  100% {
    left: 100%;
    width: 40%;
  }
`

const BarWrapper = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${({ height }) => height}px;
  overflow: hidden;
  pointer-events: none;
  z-index: 9999;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.25s ease-out;
`

const Bar = styled(Box)`
  position: absolute;
  top: 0;
  bottom: 0;
  background-color: ${({ color }) => color};
  ${({ color }) => css`
    animation: ${indeterminate} 1.2s ease-in-out infinite;
    box-shadow: 0 0 8px ${color};
  `}
`
